import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  AuthShell,
  Field,
  focusFirstError,
  PRIMARY_BUTTON_CLASS,
} from "../components/auth-form.tsx";
import { ErrorMessage } from "../components/legacy/ui.tsx";
import { authClient } from "../lib/auth-client.ts";
import { collect, type Errors, validateEmail } from "../lib/auth-validation.ts";

export const Route = createFileRoute("/forgot-password")({ component: ForgotPassword });

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Errors<"email">>({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = () => collect<"email">({ email: validateEmail(email) });

  const reset = async () => {
    setSubmitted(true);
    setFormError("");
    const found = check();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found);
      return;
    }

    setBusy(true);
    const result = await authClient.requestPasswordReset({
      email: email.trim(),
      redirectTo: `${window.location.origin}/password-reset`,
    });
    setBusy(false);
    if (result.error) setFormError(result.error.message ?? "Could not send reset email");
    else setSent(true);
  };

  // Nothing to navigate to - the next step is in their inbox - so the
  // confirmation replaces the form rather than stacking on top of a form
  // that still invites another submit.
  if (sent) {
    return (
      <AuthShell title="Check your email">
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            If an account exists for <span className="font-medium">{email.trim()}</span>, a password
            reset link is on its way.
          </p>
          <p className="text-gray-500">
            The link expires after a short while. Nothing arrived? Check your spam folder, or{" "}
            <button
              type="button"
              className="font-medium text-primary-600 hover:text-primary-500 underline"
              onClick={() => setSent(false)}
            >
              try a different address
            </button>
            .
          </p>
          <Link
            to="/sign-in"
            className="inline-block font-medium text-primary-600 hover:text-primary-500"
          >
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="Enter your email and we will send you a reset link."
    >
      <form
        noValidate
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          reset();
        }}
      >
        {formError !== "" && (
          <ErrorMessage
            heading="We ran into a problem resetting your password!"
            message={formError}
          />
        )}

        <Field
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          error={errors.email}
          onChange={(v) => {
            setEmail(v);
            if (submitted) setErrors(check());
          }}
          onBlur={() => {
            if (submitted) setErrors(check());
          }}
        />

        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={busy}>
          {busy ? "Sending…" : "Reset Password"}
        </button>
      </form>
    </AuthShell>
  );
}
